/**
 * ============================================
 * USE ALERT POLLING HOOK
 * ============================================
 * 
 * Polls the backend /api/v1/alerts endpoint to check for active alerts.
 * This is independent of environment data - alerts are global.
 * Respects user's alarm mode settings.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAlerts } from '@/context/AlertContext';
import { getAlertSettings, getUserCooldownMs } from '@/utils/notifications';
import { BACKEND_URL } from '@/config';
import type { Alert } from '@/types';

// Poll interval in milliseconds
const ALERT_POLL_INTERVAL = 10000; // 10 seconds

interface BackendAlert {
  id: string;
  service: string;
  environment_id: string;
  environment_name: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  timestamp: string;
  acknowledged: boolean;
  is_simulated: boolean;
}

interface AlertsResponse {
  active: BackendAlert[];
  has_critical: boolean;
  count: number;
  timestamp: number;
}

// Track last alarm time per alert type for cooldown mode
const lastAlarmTimes = new Map<string, number>();

// Track lastSeenAlertId per alertKey. When the engine clears an alert and
// recreates it with the same key (e.g. failover lifecycle alerts on each
// switchover), the new alert has a new ID. Comparing IDs lets us detect
// "renewed" alerts that need to re-notify even though the key was seen
// before. Tracking by key alone misses these renewals if the clear+create
// happens between two polls (so we never observe the absent state).
const lastSeenAlertIds = new Map<string, string>();

function shouldPlayAlarm(alertKey: string): boolean {
  const settings = getAlertSettings();
  
  if (!settings.enabled) return false;
  
  if (settings.alarmMode === 'every_refresh') {
    return true;
  }
  
  // Cooldown mode
  const cooldownMs = getUserCooldownMs();
  const lastTime = lastAlarmTimes.get(alertKey);
  const now = Date.now();
  
  if (!lastTime || (now - lastTime) > cooldownMs) {
    lastAlarmTimes.set(alertKey, now);
    return true;
  }
  
  return false;
}

export function useAlertPolling(disabled: boolean = false) {
  const { addAlert, clearAlert, state } = useAlerts();
  const knownAlertsRef = useRef<Set<string>>(new Set());
  const isPollingRef = useRef(false);

  const fetchAlerts = useCallback(async () => {
    if (disabled || isPollingRef.current) return;
    isPollingRef.current = true;

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/alerts`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data: AlertsResponse = await response.json();
      const settings = getAlertSettings();
      
      // Process each alert from backend
      for (const backendAlert of data.active) {
        const alertKey = `${backendAlert.environment_id}:${backendAlert.service}`;

        // Check if environment is enabled in user settings
        const envEnabled = settings.enabledEnvironments[backendAlert.environment_id] !== false;

        // Skip alerts from disabled environments entirely
        if (!envEnabled) {
          console.log(`🔕 Skipping alert for disabled environment: ${backendAlert.environment_id}`);
          // If we previously had this alert, clear it
          if (knownAlertsRef.current.has(alertKey)) {
            knownAlertsRef.current.delete(alertKey);
            lastAlarmTimes.delete(alertKey);
            lastSeenAlertIds.delete(alertKey);
          }
          continue;
        }

        // "New" can mean two things:
        //   (a) we've never seen this alertKey before (first appearance)
        //   (b) the backend cleared+recreated under the same key, so the
        //       alert.id changed even though the key didn't (this is what
        //       failover lifecycle alerts do on every switchover)
        // Tracking by id catches both cases. A poll that misses the absent
        // state (between clear and recreate) still detects the id change.
        const lastSeenId = lastSeenAlertIds.get(alertKey);
        const isNewAlert = !lastSeenId || lastSeenId !== backendAlert.id;

        // Convert backend alert to frontend format
        const alert: Alert = {
          id: backendAlert.id,
          type: `${backendAlert.service}_down`,
          severity: backendAlert.severity,
          environmentId: backendAlert.environment_id,
          environmentName: backendAlert.environment_name,
          title: backendAlert.title,
          message: backendAlert.message,
          timestamp: new Date(backendAlert.timestamp),
          acknowledged: backendAlert.acknowledged,
        };

        // Bypass grace period (and AlertContext's known-key dedupe) for any
        // NEW alert id -- regardless of severity. This is what makes the
        // failover lifecycle info-alerts actually fire device notifications
        // on EVERY switchover, not just the first one after page load.
        // triggerAlert() inside AlertContext gates playAlarm to critical
        // severity, so info alerts notify silently while criticals also
        // ring the alarm.
        if (alert.severity === 'critical' && !alert.acknowledged) {
          if (isNewAlert || shouldPlayAlarm(alertKey)) {
            console.log(`🚨 Alert polling: triggering alarm for ${alertKey}`);
            addAlert(alert, true);
          } else {
            addAlert(alert, false);
          }
        } else {
          // Info / warning alerts: bypass on first sighting of this id so
          // a notification fires; don't bypass on repeat polls so we don't
          // re-notify on every poll.
          addAlert(alert, isNewAlert);
        }

        knownAlertsRef.current.add(alertKey);
        lastSeenAlertIds.set(alertKey, backendAlert.id);
      }
      
      // Remove alerts that are no longer active
      const activeKeys = new Set(data.active.map(a => `${a.environment_id}:${a.service}`));
      for (const key of knownAlertsRef.current) {
        if (!activeKeys.has(key)) {
          knownAlertsRef.current.delete(key);
          lastAlarmTimes.delete(key);
          lastSeenAlertIds.delete(key);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      isPollingRef.current = false;
    }
  }, [addAlert, clearAlert, disabled]);

  // Initial fetch
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Polling interval
  useEffect(() => {
    const interval = setInterval(fetchAlerts, ALERT_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return {
    refetch: fetchAlerts,
    activeAlerts: state.activeAlerts,
    hasCritical: state.activeAlerts.some(a => a.severity === 'critical' && !a.acknowledged),
  };
}
